import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface WeightInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (calculatedQty: number) => void;
  productName: string;
  unitWeight: number;
}

export const WeightInputModal: React.FC<WeightInputModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  productName,
  unitWeight,
}) => {
  const [inputQty, setInputQty] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset input when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInputQty('');
    }
  }, [isOpen]);

  // Calculate total weight from quantity
  const calculatedWeight = React.useMemo(() => {
    const qty = parseInt(inputQty, 10);
    if (isNaN(qty) || qty <= 0 || unitWeight <= 0) {
      return 0;
    }
    return qty * unitWeight;
  }, [inputQty, unitWeight]);

  // Validation
  const isUnitWeightValid = unitWeight > 0 && !isNaN(unitWeight);
  const parsedQty = parseInt(inputQty, 10);
  const isInputValid = inputQty.trim() !== '' && !isNaN(parsedQty) && parsedQty > 0;

  const handleConfirm = () => {
    if (isInputValid && isUnitWeightValid) {
      onConfirm(parsedQty); // Pass quantity directly (parsed as integer)
      setInputQty('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isInputValid && isUnitWeightValid) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Súly mérése: {productName}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition"
            aria-label="Bezárás"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Unit Weight Display */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
              Egységsúly: {unitWeight} g
            </p>
          </div>

          {/* Validation Error */}
          {!isUnitWeightValid && (
            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
              <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                ⚠️ Hiba: Az egységsúly érvénytelen vagy nulla!
              </p>
            </div>
          )}

          {/* Quantity Input */}
          <div>
            <label
              htmlFor="qty-input"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              Eladott mennyiség (db)
            </label>
            <input
              ref={inputRef}
              id="qty-input"
              type="number"
              step="1"
              min="1"
              value={inputQty}
              onChange={(e) => setInputQty(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={handleKeyDown}
              className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="0"
            />
          </div>

          {/* Live Calculation Display */}
          {inputQty.trim() !== '' && isUnitWeightValid && calculatedWeight > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Becsült összsúly:</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                {calculatedWeight.toFixed(2)} g
              </p>
              {parsedQty > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  ({parsedQty} db × {unitWeight} g = {calculatedWeight.toFixed(2)} g)
                </p>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            Mégse
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isInputValid || !isUnitWeightValid}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed transition"
          >
            Rögzít ({parsedQty > 0 ? parsedQty : 0} db)
          </button>
        </div>
      </div>
    </div>
  );
};
